import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Check,
  Clock,
  Copy,
  Eye,
  FileText,
  Loader2,
  RotateCcw,
  Trash2,
  Upload,
  X,
} from 'lucide-react'

import { Button } from '@/shared/components/ui/button'
import { extractApiErrorMessage } from '@/shared/lib/api-error'
import { sha256OfFile } from '@/shared/lib/file-hash'
import { isRefusal } from '@/shared/lib/upload-errors'
import { cn } from '@/shared/lib/utils'
import {
  fileKindLabel,
  isImageValue,
  type UploadedFileItem,
} from '@/shared/lib/file-types'

export type { UploadedFileItem }

/** Passed to `uploadFn` alongside the file. */
export interface UploadAttempt {
  /**
   * Stable per picked file, across every retry of it. A transport that sends
   * it as the server's `client_upload_id` gets the file the first attempt
   * stored back from a retry, never a second copy.
   */
  clientUploadId: string
  /**
   * The file's SHA-256, when the uploader has a `duplicateCheck` and could
   * hash it. Also tells the transport to ask the server to answer a copy of a
   * file the record already holds with that file (`skip_duplicates`).
   */
  contentSha256?: string
  /** True when the uploader checks for copies: send `skip_duplicates`. */
  skipDuplicates: boolean
  /**
   * The uuid of a file on the record this upload replaces: the user chose
   * Replace for a file with the same name. The transport sends `replaces`.
   */
  replaces?: string
}

/** A file the record already holds, as the duplicate check names it. */
export interface ExistingCopy {
  fileName: string
  /** The record's own label for where the file sits, when it has one. */
  category?: string
}

/** A file on the record with the same name as a picked one. */
export interface SameNameFile extends ExistingCopy {
  uuid: string
}

/** What the record holds that matters to a batch about to be sent. */
export interface DuplicateLookup {
  /** Exact copies, keyed by SHA-256. */
  copies: Record<string, ExistingCopy>
  /** Files with the same name, keyed by lower-cased file name. */
  sameName: Record<string, SameNameFile>
  /** Whether this user may replace (soft-delete) a file on the record. */
  canReplace: boolean
  /** The most files the record may hold, and how many more it can take (null: no limit). */
  limit?: number | null
  room?: number | null
}

/** What `uploadFn` resolves with. */
export type UploadResult = UploadedFileItem & {
  /** Set when the server already held these bytes and stored nothing new. */
  existingCopy?: ExistingCopy
}

/**
 * Leave out copies before they are sent (WPS-223). Each picked file is hashed
 * in the browser; a file picked twice is sent once, and the record is asked
 * once per batch which fingerprints it already holds - those are not sent at
 * all. The copy is the caller's, since only it knows what the record is.
 */
export interface DuplicateCheck {
  /** Which of these fingerprints and names the record already holds. */
  find: (files: { sha256: string; name: string }[]) => Promise<DuplicateLookup>
  /**
   * Row text for a file the record has, e.g. "Already on this claim (Slope
   * Images) - not uploaded again".
   */
  describeExisting: (copy: ExistingCopy) => string
  /** The same-name question, e.g. "2 files have the same name as files already on this claim". */
  sameNameQuestion: (count: number) => string
  /**
   * Why a pick was stopped whole: the record has room for fewer files, e.g.
   * "This claim can hold up to 100 files. You can add 3 more."
   */
  roomMessage: (room: number, limit: number) => string
}

/**
 * The one shared file uploader. A drag/click dropzone with a centered label
 * and an acceptable-types subtitle, plus a per-file list: each row shows a
 * spinner while uploading, then a thumbnail (image) or doc icon, the file
 * name, a type caption, and View / Remove actions (Cancel while in-flight).
 *
 * It owns the upload orchestration: give it `uploadFn` and it manages the
 * in-flight state, appends finished files to `value`, and lets the user
 * cancel or remove. Works in single-file (`multiple={false}`) and multi-file
 * modes. Both the generated `FileField` (react-hook-form) and `MetadataField`
 * (prop-driven) adapt to this component, as do bespoke upload surfaces.
 *
 * Files go up a few at a time, never all at once (WPS-223): a drone job of 650
 * photos sent in parallel made every request wait on every other one, so they
 * outran the hosting proxy's deadline and starved the user's other tabs of the
 * same API.
 */

interface FileUploaderProps {
  /** Committed, already-uploaded files. */
  value: UploadedFileItem[]
  /** Called with the new committed list whenever it changes. */
  onChange: (files: UploadedFileItem[]) => void
  /** Upload transport: resolve to the stored file, throw on failure. */
  uploadFn: (file: File, attempt: UploadAttempt) => Promise<UploadResult>
  /** Leave out copies the batch or the record already has. Multi-file only. */
  duplicateCheck?: DuplicateCheck
  /**
   * Told about each file left out as a copy, and again with `delta: -1` when
   * the user sends one anyway - so a running count stays right.
   */
  onSkip?: (file: File, reason: 'repeated' | 'existing', delta: 1 | -1) => void
  /**
   * The transport forwards `attempt.clientUploadId` to the server, so an
   * upload whose answer was lost can be sent again safely. The uploader then
   * retries those itself, and reports every file that still did not upload in
   * its own list and summary - the caller must not toast per file.
   */
  retryLostUploads?: boolean
  /** Told when uploads start and when the last one settles. */
  onBusyChange?: (busy: boolean) => void
  /** Allow more than one file. Single mode replaces on a new pick. */
  multiple?: boolean
  /** HTML `accept` for the picker. */
  accept?: string
  /** Human label of accepted types shown under the dropzone (e.g. "PNG, JPG, PDF"). */
  acceptLabel?: string
  /** Overrides the main dropzone label ("Drop or click to upload"). */
  hint?: string
  disabled?: boolean
  error?: boolean
  /** Open/preview a committed file. Defaults to opening `file_url` in a tab. */
  onOpenFile?: (file: UploadedFileItem) => void
  /** Show the remove (trash) action on committed rows. Default true. Set
   *  false where a committed file is already persisted and shouldn't be
   *  deleted from this surface (e.g. an append-only "add" modal). */
  canRemove?: boolean
  /** Custom remove handler for committed rows. When provided, clicking the
   *  trash calls this instead of dropping the row from the list locally - the
   *  caller owns the removal (e.g. a confirm dialog + server delete) and
   *  reflects it back through `onChange`. Omit for the default local remove. */
  onRemove?: (file: UploadedFileItem, index: number) => void
  className?: string
}

/** A picked file that has not landed in `value` yet. */
type PendingUpload = {
  id: number
  file: File
  clientUploadId: string
  status:
    | 'checking'
    | 'queued'
    | 'uploading'
    | 'retrying'
    | 'error'
    | 'duplicate'
    | 'same-name'
  /** Attempts made so far. */
  attempts: number
  /** SHA-256 of the file, once the duplicate check has hashed it. */
  sha256?: string
  /** Why a duplicate row was left out; only one already on the record can be sent anyway. */
  duplicateOf?: 'existing' | 'repeated'
  /** Sent although it is a copy: the user chose "Upload anyway". */
  force?: boolean
  /** A same-name row: the file on the record it would replace. */
  sameNameAs?: SameNameFile
  /** The user chose Replace: the uuid of the file this upload replaces. */
  replaces?: string
  /**
   * Why it did not upload (error rows of a `retryLostUploads` uploader), or
   * which file it is a copy of (duplicate rows).
   */
  message?: string
  /** False when the server refused the file itself - sending it again cannot help. */
  retryable?: boolean
}

// Parallel uploads. Enough to keep a fast line busy; few enough that each one
// finishes well inside the hosting proxy's 60 s deadline on an ordinary office
// connection, and that the browser keeps capacity for the user's other tabs.
const MAX_PARALLEL = 3
// Attempts per file before it is listed as not uploaded.
const MAX_ATTEMPTS = 6
// After a lost upload the whole queue pauses, longer each time it happens
// again, back to none on the next success. A dropped connection then holds the
// batch instead of failing every waiting file through in seconds.
const PAUSE_STEPS_MS = [2000, 5000, 10000, 20000, 30000]
// Queued rows are listed one by one only for a small batch; a large one is a
// count in the summary.
const MAX_LISTED_QUEUED = 10
// Files hashed at once by the duplicate check. Each is read whole into memory.
const HASH_PARALLEL = 4

type Batch = {
  /** Every file picked into this window, copies included. */
  total: number
  done: number
  /** Copies left out: already on the record, or picked twice. */
  skipped: number
  /** Duplicate check progress while it hashes the picked files. */
  checked: number
  checking: number
}

const EMPTY_BATCH: Batch = { total: 0, done: 0, skipped: 0, checked: 0, checking: 0 }

let uploadIdSeq = 0
const nextUploadId = () => (uploadIdSeq += 1)

export function FileUploader({
  value,
  onChange,
  uploadFn,
  retryLostUploads = false,
  duplicateCheck,
  onSkip,
  onBusyChange,
  multiple = false,
  accept,
  acceptLabel,
  hint,
  disabled = false,
  error = false,
  onOpenFile,
  canRemove = true,
  onRemove,
  className,
}: FileUploaderProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragActive, setDragActive] = useState(false)
  const [pending, setPending] = useState<PendingUpload[]>([])
  // Files picked and files uploaded since the uploader opened, across every
  // pick - the denominator and numerator of the summary.
  const [batch, setBatch] = useState<Batch>(EMPTY_BATCH)
  // Whether the record lets this user replace a file (offered as Replace).
  const [canReplace, setCanReplace] = useState(false)
  // Why the last pick was stopped whole, when the record had too little room.
  const [noRoom, setNoRoom] = useState<string | null>(null)

  // Latest committed value, read inside async upload callbacks so concurrent
  // uploads append correctly. Kept in sync after each render, and written
  // synchronously on commit/remove so back-to-back resolves see each other.
  const valueRef = useRef(value)
  useEffect(() => {
    valueRef.current = value
  }, [value])

  // The queue lives in refs so async callbacks always see the current one;
  // `pending` mirrors it for rendering.
  const itemsRef = useRef<PendingUpload[]>([])
  const activeRef = useRef(0)
  const limitRef = useRef(MAX_PARALLEL)
  const streakRef = useRef(0)
  const pauseStepRef = useRef(0)
  const pausedRef = useRef(false)
  const resumeTimerRef = useRef<number | undefined>(undefined)
  const cancelledRef = useRef<Set<number>>(new Set())
  const mountedRef = useRef(true)
  // Latest props for the async callbacks.
  const uploadFnRef = useRef(uploadFn)
  const onChangeRef = useRef(onChange)
  const checkRef = useRef(duplicateCheck)
  const onSkipRef = useRef(onSkip)
  useEffect(() => {
    uploadFnRef.current = uploadFn
    onChangeRef.current = onChange
    checkRef.current = duplicateCheck
    onSkipRef.current = onSkip
  }, [uploadFn, onChange, duplicateCheck, onSkip])
  // The fingerprint of each committed file, so a later pick of the same photo
  // reads as a copy of it. Keyed by uuid: a removed file stops counting.
  const committedShaRef = useRef<Map<string, { sha256: string; name: string }>>(new Map())

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      window.clearTimeout(resumeTimerRef.current)
    }
  }, [])

  const sync = () => {
    if (mountedRef.current) setPending([...itemsRef.current])
  }
  const patch = (id: number, changes: Partial<PendingUpload>) => {
    itemsRef.current = itemsRef.current.map((p) => (p.id === id ? { ...p, ...changes } : p))
    sync()
  }
  const drop = (id: number) => {
    itemsRef.current = itemsRef.current.filter((p) => p.id !== id)
    sync()
  }

  const commit = (item: UploadedFileItem) => {
    const next = multiple ? [...valueRef.current, item] : [item]
    valueRef.current = next
    onChangeRef.current(next)
  }

  // The pause is over: files waiting to be sent again rejoin the queue in
  // their original place.
  const resume = () => {
    pausedRef.current = false
    itemsRef.current = itemsRef.current.map((p) =>
      p.status === 'retrying' ? { ...p, status: 'queued' } : p,
    )
    sync()
    pump()
  }

  const pump = () => {
    if (!mountedRef.current || pausedRef.current) return
    while (activeRef.current < limitRef.current) {
      const next = itemsRef.current.find((p) => p.status === 'queued')
      if (!next) return
      start(next)
    }
  }

  const start = (item: PendingUpload) => {
    const { id } = item
    activeRef.current += 1
    patch(id, { status: 'uploading', attempts: item.attempts + 1 })
    // Inside a promise so a transport that throws before returning one still
    // settles below and frees its slot, instead of stalling the queue.
    Promise.resolve()
      .then(() =>
        uploadFnRef.current(item.file, {
          clientUploadId: item.clientUploadId,
          contentSha256: item.sha256,
          // "Upload anyway" sends a copy on purpose.
          skipDuplicates: !!checkRef.current && !item.force,
          replaces: item.replaces,
        }),
      )
      .then((result) => {
        if (cancelledRef.current.delete(id)) return
        if (result.existingCopy) {
          // Someone else put the same photo on the record meanwhile: the
          // server stored nothing and named the file it already had.
          markExisting(item, result.existingCopy)
        } else {
          const stored: UploadedFileItem = {
            uuid: result.uuid,
            file_name: result.file_name,
            file_url: result.file_url,
            thumbnail_url: result.thumbnail_url,
            mime_type: result.mime_type,
          }
          if (item.sha256 && stored.uuid) {
            committedShaRef.current.set(stored.uuid, {
              sha256: item.sha256,
              name: item.file.name,
            })
          }
          commit(stored)
          drop(id)
          setBatch((b) => ({ ...b, done: b.done + 1 }))
        }
        pauseStepRef.current = 0
        // Back up to full speed after a run of clean uploads.
        streakRef.current += 1
        if (streakRef.current >= 5 && limitRef.current < MAX_PARALLEL) {
          limitRef.current += 1
          streakRef.current = 0
        }
      })
      .catch((err: unknown) => {
        if (cancelledRef.current.delete(id)) return
        streakRef.current = 0
        const refused = isRefusal(err)
        if (!refused) {
          // The line or the server is struggling: send fewer at once.
          limitRef.current = Math.max(1, Math.floor(limitRef.current / 2))
        }
        const current = itemsRef.current.find((p) => p.id === id)
        const attempts = current?.attempts ?? MAX_ATTEMPTS
        if (retryLostUploads && !refused && attempts < MAX_ATTEMPTS) {
          // Hold the queue, then send this file again with the same key.
          // Uploads lost together (one dropped connection) count as one pause.
          patch(id, { status: 'retrying' })
          if (!pausedRef.current) {
            pausedRef.current = true
            const step = Math.min(pauseStepRef.current, PAUSE_STEPS_MS.length - 1)
            pauseStepRef.current += 1
            window.clearTimeout(resumeTimerRef.current)
            resumeTimerRef.current = window.setTimeout(resume, PAUSE_STEPS_MS[step])
          }
          return
        }
        if (!retryLostUploads && refused) {
          // The caller's toast says why; a file the server refused (too large,
          // wrong type) can never succeed, so its row goes (WPS-190 #18).
          drop(id)
          return
        }
        patch(id, {
          status: 'error',
          retryable: !refused,
          message: retryLostUploads
            ? refused
              ? (extractApiErrorMessage(err) ??
                t('fileUploader.refused', 'This file cannot be uploaded.'))
              : t(
                  'fileUploader.connectionFailed',
                  'Not uploaded - check your connection, then retry',
                )
            : undefined,
        })
      })
      .finally(() => {
        activeRef.current -= 1
        pump()
      })
  }

  const skip = (item: PendingUpload, reason: 'existing' | 'repeated', message: string) => {
    patch(item.id, { status: 'duplicate', duplicateOf: reason, message })
    setBatch((b) => ({ ...b, skipped: b.skipped + 1 }))
    onSkipRef.current?.(item.file, reason, 1)
  }

  const markExisting = (item: PendingUpload, copy: ExistingCopy) =>
    skip(
      item,
      'existing',
      checkRef.current?.describeExisting(copy) ??
        t('fileUploader.alreadyHere', 'Already uploaded here - not uploaded again'),
    )

  // The rare copy that is wanted after all: send it, and stop counting it as
  // skipped.
  const uploadAnyway = (id: number) => {
    const item = itemsRef.current.find((p) => p.id === id)
    if (!item || item.duplicateOf !== 'existing') return
    patch(id, { status: 'queued', force: true, duplicateOf: undefined, message: undefined })
    setBatch((b) => ({ ...b, skipped: Math.max(0, b.skipped - 1) }))
    onSkipRef.current?.(item.file, 'existing', -1)
    pump()
  }

  // One answer for every file waiting on the same-name question: Replace
  // sends each as the new version of the file it shares a name with; Keep
  // both stores it alongside.
  const answerSameName = (choice: 'replace' | 'keep') => {
    itemsRef.current = itemsRef.current.map((p) =>
      p.status === 'same-name'
        ? {
            ...p,
            status: 'queued',
            replaces: choice === 'replace' ? p.sameNameAs?.uuid : undefined,
          }
        : p,
    )
    sync()
    pump()
  }

  /** The name of a file this uploader already has with these bytes, if any. */
  const knownCopy = (sha256: string, exclude: Set<number>): string | undefined => {
    const pendingCopy = itemsRef.current.find(
      (p) => p.sha256 === sha256 && p.status !== 'duplicate' && !exclude.has(p.id),
    )
    if (pendingCopy) return pendingCopy.file.name
    const live = new Set(valueRef.current.map((v) => v.uuid))
    for (const [uuid, entry] of committedShaRef.current) {
      if (entry.sha256 === sha256 && live.has(uuid)) return entry.name
    }
    return undefined
  }

  // Hash the picked files, leave out the copies, hold the ones that share a
  // name with a different file on the record, then queue the rest. One
  // question to the server for the whole pick, never one per file.
  const checkCopies = async (ids: number[]) => {
    const check = checkRef.current
    const group = new Set(ids)
    const live = () => itemsRef.current.filter((p) => group.has(p.id) && p.status === 'checking')

    const toHash = live()
    let next = 0
    const hashOne = async () => {
      while (next < toHash.length && mountedRef.current) {
        const item = toHash[next]
        next += 1
        const sha256 = await sha256OfFile(item.file)
        if (sha256) patch(item.id, { sha256 })
        setBatch((b) => ({ ...b, checked: b.checked + 1 }))
      }
    }
    await Promise.all(Array.from({ length: HASH_PARALLEL }, hashOne))
    if (!mountedRef.current) return

    // A photo picked twice is sent once; the second row names the first.
    const firstOfEach = new Map<string, string>()
    for (const item of live()) {
      if (!item.sha256) continue
      const sameAs = knownCopy(item.sha256, group) ?? firstOfEach.get(item.sha256)
      if (sameAs) {
        skip(
          item,
          'repeated',
          t('fileUploader.sameAs', 'Same photo as {{name}} - skipped', { name: sameAs }),
        )
      } else {
        firstOfEach.set(item.sha256, item.file.name)
      }
    }

    let found: DuplicateLookup = { copies: {}, sameName: {}, canReplace: false }
    const toAsk = live().filter((p) => p.sha256)
    if (check && toAsk.length > 0) {
      try {
        found = await check.find(toAsk.map((p) => ({ sha256: p.sha256!, name: p.file.name })))
      } catch {
        // Send them anyway: the server checks each one as it arrives.
      }
    }
    if (!mountedRef.current) return
    // Room for fewer new files than this pick holds: none of it goes, so the
    // user picks fewer rather than getting an arbitrary part of it.
    const fresh = live().filter(
      (p) => !(p.sha256 && found.copies[p.sha256]) && !found.sameName[p.file.name.toLowerCase()],
    )
    if (check && found.room != null && fresh.length > found.room) {
      const repeats = itemsRef.current.filter((p) => group.has(p.id) && p.status === 'duplicate')
      repeats.forEach((p) => onSkipRef.current?.(p.file, 'repeated', -1))
      itemsRef.current = itemsRef.current.filter((p) => !group.has(p.id))
      sync()
      setBatch((b) => ({
        ...b,
        total: Math.max(b.done, b.total - ids.length),
        skipped: Math.max(0, b.skipped - repeats.length),
        checking: Math.max(0, b.checking - ids.length),
        checked: Math.max(0, b.checked - ids.length),
      }))
      setNoRoom(check.roomMessage(found.room, found.limit ?? found.room))
      return
    }
    for (const item of live()) {
      const copy = item.sha256 ? found.copies[item.sha256] : undefined
      const sameName = found.sameName[item.file.name.toLowerCase()]
      if (copy) {
        markExisting(item, copy)
      } else if (sameName) {
        // A different file with this name is on the record - most often a
        // newer version of a document. Ask once, below; the rest go on.
        patch(item.id, { status: 'same-name', sameNameAs: sameName })
      } else {
        patch(item.id, { status: 'queued' })
      }
    }
    if (found.canReplace) setCanReplace(true)
    setBatch((b) => ({
      ...b,
      checking: Math.max(0, b.checking - ids.length),
      checked: Math.max(0, b.checked - ids.length),
    }))
    pump()
  }

  const enqueue = (files: File[]) => {
    setNoRoom(null)
    const check = multiple && !!checkRef.current
    const added = files.map<PendingUpload>((file) => ({
      id: nextUploadId(),
      file,
      clientUploadId: crypto.randomUUID(),
      status: check ? 'checking' : 'queued',
      attempts: 0,
    }))
    itemsRef.current = [...itemsRef.current, ...added]
    const checking = check ? added.length : 0
    // Every pick adds to the same count, so a second or third drop into the
    // same window reads "33 of 33", never just the last pick's "21 of 21".
    setBatch((b) => ({ ...b, total: b.total + added.length, checking: b.checking + checking }))
    sync()
    if (check) void checkCopies(added.map((a) => a.id))
    else pump()
  }

  const acceptFiles = (files: File[]) => {
    if (disabled || files.length === 0) return
    if (!multiple) {
      // Replace: abandon any in-flight upload and the current committed file.
      itemsRef.current.forEach((p) => cancelledRef.current.add(p.id))
      itemsRef.current = []
      valueRef.current = []
      onChange([])
      enqueue([files[0]])
      return
    }
    enqueue(files)
  }

  const removeCommitted = (index: number) => {
    const next = value.filter((_, i) => i !== index)
    valueRef.current = next
    onChange(next)
  }

  const cancelPending = (id: number) => {
    const item = itemsRef.current.find((p) => p.id === id)
    if (item?.status === 'uploading') cancelledRef.current.add(id)
    drop(id)
    // A copy row stays counted: it was picked, and it was skipped.
    if (item?.status === 'duplicate') return
    setBatch((b) => ({ ...b, total: Math.max(b.done, b.total - 1) }))
  }

  const retryFailed = () => {
    itemsRef.current = itemsRef.current.map((p) =>
      p.status === 'error' && p.retryable !== false
        ? { ...p, status: 'queued', attempts: 0, message: undefined }
        : p,
    )
    sync()
    pump()
  }

  const dismissFailed = () => {
    const failedCount = itemsRef.current.filter((p) => p.status === 'error').length
    itemsRef.current = itemsRef.current.filter(
      (p) => p.status !== 'error' && p.status !== 'duplicate',
    )
    setBatch((b) => ({ ...b, total: Math.max(b.done, b.total - failedCount) }))
    sync()
  }

  const openFile = (file: UploadedFileItem) => {
    if (onOpenFile) return onOpenFile(file)
    if (file.file_url) window.open(file.file_url, '_blank', 'noopener,noreferrer')
  }

  const failed = pending.filter((p) => p.status === 'error')
  const copies = pending.filter((p) => p.status === 'duplicate')
  const queued = pending.filter((p) => p.status === 'queued')
  const checking = pending.filter((p) => p.status === 'checking')
  const sameName = pending.filter((p) => p.status === 'same-name')
  // Sending, or about to. A same-name file waiting on the user's answer is not
  // working, but it is not on the record yet either - closing would drop it.
  const working = pending.some((p) =>
    ['checking', 'queued', 'uploading', 'retrying'].includes(p.status),
  )
  const busy = working || sameName.length > 0
  const retryableFailed = failed.filter((p) => p.retryable !== false).length

  useEffect(() => {
    onBusyChange?.(busy)
  }, [busy, onBusyChange])

  // Leaving the page drops everything not yet sent, so ask first.
  useEffect(() => {
    if (!busy) return
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      // Older Chrome and Safari only prompt when returnValue is set.
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [busy])

  const skipped = batch.skipped
  const showSummary = multiple && (batch.total > 1 || failed.length > 0 || skipped > 0)
  const listQueued = queued.length <= MAX_LISTED_QUEUED
  const listChecking = checking.length <= MAX_LISTED_QUEUED
  const rows = [
    ...failed,
    ...copies,
    ...pending.filter((p) => p.status === 'uploading' || p.status === 'retrying'),
    ...(listChecking ? checking : []),
    ...(listQueued ? queued : []),
  ]

  const hasAny = value.length > 0 || pending.length > 0
  const showDropzone = multiple || !hasAny

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {showDropzone && (
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            'h-auto w-full flex-col justify-center gap-1 border-dashed py-6 transition-colors',
            dragActive
              ? 'border-[var(--green)] bg-[var(--green-bg)] text-[var(--green)]'
              : error
                ? 'border-[var(--status-error)] text-[var(--ink-3)]'
                : 'text-[var(--ink-3)]',
          )}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault()
            if (!dragActive && !disabled) setDragActive(true)
          }}
          onDragLeave={(e) => {
            e.preventDefault()
            if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
            setDragActive(false)
          }}
          onDrop={(e) => {
            e.preventDefault()
            setDragActive(false)
            const dropped = Array.from(e.dataTransfer.files ?? [])
            if (dropped.length) acceptFiles(dropped)
          }}
        >
          <Upload className="h-5 w-5 opacity-60" />
          <span className="whitespace-normal text-center text-[13px] font-medium text-[var(--ink-2)]">
            {dragActive
              ? multiple
                ? 'Drop files to upload'
                : 'Drop file to upload'
              : hint || 'Drop or click to upload'}
          </span>
          {acceptLabel && (
            <span className="max-w-full whitespace-normal break-words px-4 text-center text-[11px] font-normal normal-case leading-snug text-[var(--ink-3)]">
              {acceptLabel}
              {multiple ? ' - single or multiple' : ''}
            </span>
          )}
        </Button>
      )}

      {showSummary && (
        <div
          role="status"
          aria-live="polite"
          className="flex flex-col gap-2 rounded-[8px] border border-input bg-[var(--bg-subtle)] p-2.5"
        >
          <div className="flex items-center gap-2">
            {working ? (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[var(--green)]" />
            ) : sameName.length > 0 ? (
              <Clock className="h-4 w-4 shrink-0 text-[var(--status-warn)]" />
            ) : failed.length > 0 ? (
              <X className="h-4 w-4 shrink-0 text-[var(--status-error)]" />
            ) : (
              <Check className="h-4 w-4 shrink-0 text-[var(--green)]" />
            )}
            <span className="min-w-0 flex-1 text-[13px] text-[var(--ink)]">
              {checking.length > 0
                ? t('fileUploader.checkingCount', 'Checking {{done}} of {{total}} for copies', {
                    done: batch.checked,
                    total: batch.checking,
                  })
                : working
                  ? t('fileUploader.uploadingCount', 'Uploading {{done}} of {{total}}', batch)
                  : batch.done === 0 && skipped > 0 && skipped === batch.total
                    ? t('fileUploader.nothingNew', 'Nothing new to upload')
                    : t('fileUploader.uploadedCount', '{{done}} of {{total}} uploaded', batch)}
              {skipped > 0 && (
                <span className="text-[var(--ink-3)]">
                  {' - '}
                  {t('fileUploader.skippedCount', '{{count}} duplicates skipped', {
                    count: skipped,
                  })}
                </span>
              )}
              {sameName.length > 0 && (
                <span className="text-[var(--ink-3)]">
                  {' - '}
                  {t('fileUploader.awaitingAnswerCount', '{{count}} waiting for your answer', {
                    count: sameName.length,
                  })}
                </span>
              )}
              {failed.length > 0 && (
                <span className="text-[var(--status-error)]">
                  {' - '}
                  {t('fileUploader.failedCount', '{{count}} not uploaded', {
                    count: failed.length,
                  })}
                </span>
              )}
              {!listQueued && (
                <span className="text-[var(--ink-3)]">
                  {' - '}
                  {t('fileUploader.waitingCount', '{{count}} waiting', {
                    count: queued.length,
                  })}
                </span>
              )}
            </span>
            {retryableFailed > 0 && (
              <Button type="button" variant="outline" size="xs" onClick={retryFailed}>
                <RotateCcw className="h-3.5 w-3.5" />
                {t('fileUploader.retryFailed', 'Retry failed')}
              </Button>
            )}
            {(failed.length > 0 || copies.length > 0) && !busy && (
              <Button type="button" variant="ghost" size="xs" onClick={dismissFailed}>
                {t('fileUploader.dismissFailed', 'Dismiss')}
              </Button>
            )}
          </div>
          {working && checking.length === 0 && batch.total > 0 && (
            <div className="h-1 overflow-hidden rounded-full bg-[var(--rule)]">
              <div
                className="h-full rounded-full bg-[var(--green)] transition-[width] duration-300"
                style={{ width: `${Math.round((batch.done / batch.total) * 100)}%` }}
              />
            </div>
          )}
        </div>
      )}

      {noRoom && (
        <div
          role="alert"
          className="flex items-center gap-2 rounded-[8px] border border-[var(--status-error-border)] bg-[var(--status-error-bg)] p-2.5"
        >
          <X className="h-4 w-4 shrink-0 text-[var(--status-error)]" />
          <span className="min-w-0 flex-1 text-[13px] text-[var(--ink)]">{noRoom}</span>
          <Button type="button" variant="ghost" size="xs" onClick={() => setNoRoom(null)}>
            {t('fileUploader.dismissFailed', 'Dismiss')}
          </Button>
        </div>
      )}

      {sameName.length > 0 && (
        <div
          role="group"
          aria-label={duplicateCheck?.sameNameQuestion(sameName.length)}
          className="flex flex-col gap-2 rounded-[8px] border border-[var(--status-warn)] bg-[var(--status-warn-bg)] p-3"
        >
          <p className="text-[13px] font-medium text-[var(--ink)]">
            {duplicateCheck?.sameNameQuestion(sameName.length)}
          </p>
          <ul className="flex flex-col gap-0.5 text-[12px] text-[var(--ink-2)]">
            {sameName.slice(0, MAX_LISTED_QUEUED).map((p) => (
              <li key={`same-name-${p.id}`} className="truncate">
                {p.file.name}
                {p.sameNameAs?.category ? ` (${p.sameNameAs.category})` : ''}
              </li>
            ))}
            {sameName.length > MAX_LISTED_QUEUED && (
              <li>
                {t('fileUploader.andMore', 'and {{count}} more', {
                  count: sameName.length - MAX_LISTED_QUEUED,
                })}
              </li>
            )}
          </ul>
          <div className="flex flex-wrap items-center gap-2">
            {canReplace && (
              <Button type="button" size="xs" onClick={() => answerSameName('replace')}>
                {t('fileUploader.replace', 'Replace')}
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={() => answerSameName('keep')}
            >
              {t('fileUploader.keepBoth', 'Keep both')}
            </Button>
            {canReplace && (
              <span className="text-[11px] text-[var(--ink-3)]">
                {t(
                  'fileUploader.replaceHelp',
                  'Replace takes the older file off the list; it stays in the history.',
                )}
              </span>
            )}
          </div>
        </div>
      )}

      {hasAny && (
        <ul className="flex flex-col gap-1.5">
          {rows.map((p) => (
            <li
              key={`pending-${p.id}`}
              className="flex items-center gap-3 rounded-[8px] border border-input bg-[var(--bg-subtle)] p-2.5"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[6px] border border-input bg-[var(--bg)]">
                {p.status === 'error' ? (
                  <X className="h-4 w-4 text-[var(--status-error)]" />
                ) : p.status === 'duplicate' ? (
                  <Copy className="h-4 w-4 text-[var(--status-warn)]" />
                ) : p.status === 'queued' ? (
                  <Clock className="h-4 w-4 text-[var(--ink-3)]" />
                ) : (
                  <Loader2 className="h-4 w-4 animate-spin text-[var(--green)]" />
                )}
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-[13px] text-[var(--ink)]">{p.file.name}</span>
                {p.status === 'error' && p.message ? (
                  <span className="text-[11px] text-[var(--status-error)]">{p.message}</span>
                ) : p.status === 'duplicate' ? (
                  <span className="text-[11px] text-[var(--ink-2)]">
                    {p.message}
                    {p.duplicateOf === 'existing' && (
                      <Button
                        type="button"
                        variant="link"
                        size="xs"
                        className="ml-1 h-auto p-0 text-[11px]"
                        onClick={() => uploadAnyway(p.id)}
                      >
                        {t('fileUploader.uploadAnyway', 'Upload anyway')}
                      </Button>
                    )}
                  </span>
                ) : (
                  <span className="text-[11px] uppercase tracking-[0.06em] text-[var(--ink-3)]">
                    {p.status === 'error'
                      ? t('fileUploader.uploadFailed', 'Upload failed')
                      : p.status === 'checking'
                        ? t('fileUploader.checking', 'Checking for copies')
                        : p.status === 'queued'
                          ? t('fileUploader.waiting', 'Waiting')
                          : p.status === 'retrying'
                            ? t('fileUploader.retrying', 'Retrying')
                            : `${fileKindLabel(p.file.type, p.file.name)} - uploading...`}
                  </span>
                )}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label={
                  p.status === 'duplicate' ? `Dismiss ${p.file.name}` : `Cancel ${p.file.name}`
                }
                onClick={() => cancelPending(p.id)}
              >
                <X className="h-3.5 w-3.5 text-[var(--ink-3)]" />
              </Button>
            </li>
          ))}
          {value.map((item, index) => {
            const image = isImageValue(item.mime_type, item.file_name)
            // The thumbnail, never the full photo: a 650-photo batch would
            // otherwise download every original again just to draw 36px icons.
            const preview = item.thumbnail_url || item.file_url
            return (
              <li
                key={item.uuid || `committed-${index}`}
                className="flex items-center gap-3 rounded-[8px] border border-input bg-[var(--bg-subtle)] p-2.5"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-[6px] border border-input bg-[var(--bg)]">
                  {image && preview ? (
                    <img
                      src={preview}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <FileText className="h-4 w-4 text-[var(--ink-3)]" />
                  )}
                </span>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-[13px] text-[var(--ink)]">
                    {item.file_name}
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.06em] text-[var(--ink-3)]">
                    {fileKindLabel(item.mime_type, item.file_name)}
                  </span>
                </span>
                {item.file_url && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    aria-label={`View ${item.file_name}`}
                    onClick={() => openFile(item)}
                  >
                    <Eye className="h-3.5 w-3.5 text-[var(--ink-3)]" />
                  </Button>
                )}
                {canRemove && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    disabled={disabled}
                    aria-label={`Remove ${item.file_name}`}
                    onClick={() =>
                      onRemove ? onRemove(item, index) : removeCommitted(index)
                    }
                  >
                    <Trash2 className="h-3.5 w-3.5 text-[var(--status-error)]" />
                  </Button>
                )}
              </li>
            )
          })}
        </ul>
      )}

      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        accept={accept}
        className="hidden"
        onChange={(event) => {
          const files = Array.from(event.target.files ?? [])
          acceptFiles(files)
          event.currentTarget.value = ''
        }}
      />
    </div>
  )
}
